/* Money — ADR-0005.
 *
 * An integer in the minor unit plus an ISO 4217 code. Never a float, never a
 * decimal string past the boundary, never a pre-formatted display value.
 *
 * ── the three things this file exists to stop ────────────────────────────────
 * 1. **A hardcoded two decimal places.** JPY has none, KWD has three. The digit
 *    count comes from the currency, at runtime, from the platform.
 * 2. **A hardcoded currency list.** Every ISO 4217 currency is supported from
 *    day one and the set comes from `Intl.supportedValuesOf('currency')`. A list
 *    committed to the repo is stale the day a customer signs up outside it.
 *    Which currencies an Organisation *offers* is that Organisation's data, and
 *    it lives nowhere near here.
 * 3. **Adding two currencies together.** Refused, not coerced. The product
 *    applies no exchange rate anywhere; it is not a currency authority.
 */
import { dinero, add as dineroAdd, subtract as dineroSubtract, toSnapshot } from 'dinero.js';
/* Minor-unit digits, from the platform, memoised. `Intl.NumberFormat` is the
 * source: it already carries the ISO 4217 table and is always current with the
 * runtime. Resolving it costs enough to be worth caching and never changes
 * within a process. */
const digitsCache = new Map();
export function minorUnitDigits(currency) {
    const cached = digitsCache.get(currency);
    if (cached !== undefined)
        return cached;
    const digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits;
    /* Deliberately not `?? 2`. A runtime that cannot say how many minor units a
     * currency has is a runtime we cannot format money on, and defaulting to two
     * would silently produce the exact bug this module exists to prevent — a
     * hundredfold error in JPY. Unexpected, therefore thrown. */
    if (digits === undefined) {
        throw new Error(`The runtime reports no minor-unit digit count for ${currency}. ` +
            'Money cannot be represented without it.');
    }
    digitsCache.set(currency, digits);
    return digits;
}
/**
 * Parses an untrusted currency code.
 *
 * Checked against the platform's own list rather than a regex: `XYZ` is three
 * uppercase letters and is not a currency. Returns null on anything else —
 * an expected outcome, not an exception.
 */
export function parseCurrency(value) {
    if (typeof value !== 'string')
        return null;
    const code = value.toUpperCase();
    return supportedCurrencies().has(code) ? code : null;
}
let supported = null;
/** Every ISO 4217 currency the runtime knows. Never a constant in this repo. */
export function supportedCurrencies() {
    supported ??= new Set(Intl.supportedValuesOf('currency'));
    return supported;
}
/** Builds Money from an already-validated integer amount and currency. */
export function money(amount, currency) {
    if (!Number.isSafeInteger(amount)) {
        throw new TypeError(`Money must be a safe integer in the minor unit; got ${amount} ${currency}. ` +
            'A fractional amount here means a float leaked past a boundary.');
    }
    const exponent = minorUnitDigits(currency);
    return dinero({ amount, currency: { code: currency, base: 10, exponent } });
}
export function toMoneySnapshot(value) {
    const snapshot = toSnapshot(value);
    return { amount: snapshot.amount, currency: snapshot.currency.code };
}
export const currencyOf = (value) => toSnapshot(value).currency.code;
export const amountOf = (value) => toSnapshot(value).amount;
/* ── arithmetic ─────────────────────────────────────────────────────────────
 * Mixing currencies throws rather than returning a result, and that is the one
 * place in this file where throwing is right: it is not an expected outcome a
 * caller should branch on, it is a bug in the caller. Reporting groups by
 * currency precisely so this is unreachable in normal operation. */
function assertSameCurrency(a, b) {
    const left = currencyOf(a);
    const right = currencyOf(b);
    if (left !== right) {
        throw new TypeError(`Refusing to combine ${left} with ${right}. Totals are never summed across ` +
            'currencies and no exchange rate is applied anywhere — ADR-0005.');
    }
}
export function add(a, b) {
    assertSameCurrency(a, b);
    return dineroAdd(a, b);
}
export function subtract(a, b) {
    assertSameCurrency(a, b);
    return dineroSubtract(a, b);
}
/** Sums money of one currency. An empty list needs the currency stated. */
export function sum(values, currency) {
    return values.reduce((total, value) => add(total, value), money(0, currency));
}
/**
 * Parses what a person typed into Money, in their locale.
 *
 * **The separators come from the locale and this is not a detail.** `1.234,50`
 * is one thousand in Turkish and malformed in English; the same keystrokes are
 * two different numbers in the two languages this product ships (ADR-0014). So
 * the locale is a required argument — there is no sensible default, and a
 * default would be silently wrong for half the users.
 */
export function parseMoney(input, currency, locale) {
    const trimmed = input.trim();
    if (trimmed === '')
        return { ok: false, error: 'empty' };
    const { decimal, group } = separatorsFor(locale);
    const digits = minorUnitDigits(currency);
    /* Strip grouping, normalise the decimal separator, and reject anything else.
     * Deliberately not a Number() call on the raw string: `lang-typescript.md`
     * bans that on money, because Number('1,5') is NaN in one locale's input and
     * 15 in another's after a naive strip. */
    const normalised = trimmed.split(group).join('').replace(decimal, '.');
    if (!/^-?\d*(\.\d*)?$/.test(normalised) || normalised === '-' || normalised === '.') {
        return { ok: false, error: 'not-a-number' };
    }
    const [whole = '', fraction = ''] = normalised.replace('-', '').split('.');
    if (fraction.length > digits)
        return { ok: false, error: 'too-many-decimals' };
    const minor = `${whole}${fraction.padEnd(digits, '0')}` || '0';
    const amount = Number.parseInt(minor, 10) * (normalised.startsWith('-') ? -1 : 1);
    if (!Number.isSafeInteger(amount))
        return { ok: false, error: 'too-large' };
    return { ok: true, value: money(amount, currency) };
}
/** Formats Money for display, in the reader's locale. */
export function formatMoney(value, locale) {
    const { amount, currency } = toMoneySnapshot(value);
    const digits = minorUnitDigits(currency);
    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
    });
    /* Handed over as a string, never as a Number: dividing by 10^digits is a
     * float operation on a value the whole file exists to keep off floats. */
    return formatter.format(toDecimalString(amount, digits));
}
/** Integer minor units to a plain decimal string, by digit surgery. */
export function toDecimalString(amount, digits) {
    const negative = amount < 0;
    const padded = String(Math.abs(amount)).padStart(digits + 1, '0');
    const whole = digits === 0 ? padded : padded.slice(0, -digits);
    const fraction = digits === 0 ? '' : `.${padded.slice(-digits)}`;
    return `${negative ? '-' : ''}${whole}${fraction}`;
}
const separatorCache = new Map();
function separatorsFor(locale) {
    const cached = separatorCache.get(locale);
    if (cached !== undefined)
        return cached;
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const separators = {
        decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
        group: parts.find((p) => p.type === 'group')?.value ?? ',',
    };
    separatorCache.set(locale, separators);
    return separators;
}
//# sourceMappingURL=money.js.map